import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './AccountHistory.css';
import { logout, checkAuth, handleAuthError } from '../utils/auth';
import { API_URL } from '../config/data';
import OperationCard from '../components/OperationCard';
import ProfitCard from '../components/ProfitCard';

const AccountHistory = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam === 'profits' ? 1 : 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [operations, setOperations] = useState([]);
  const [profits, setProfits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Функция для получения токена из localStorage
  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  // Функция для получения профиля пользователя
  const fetchUserProfile = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.user;
    } catch (error) {
      console.error('Ошибка при получении профиля:', error);
      throw error;
    }
  };

  // Функция для получения операций
  const fetchOperations = async (type = null) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const params = new URLSearchParams();
      if (type) {
        params.append('type', type);
      }
      params.append('limit', '50'); // Получаем больше записей для истории

      const response = await axios.get(`${API_URL}/api/operations?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.operations || [];
    } catch (error) {
      console.error('Ошибка при получении операций:', error);
      throw error;
    }
  };

  // Функция для получения доходов
  const fetchProfits = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/profile/profits`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data.profits || [];
    } catch (error) {
      console.error('Ошибка при получении доходов:', error);
      throw error;
    }
  };

  // Функция для получения имени пользователя
  const getUserDisplayName = () => {
    if (!user) return 'Загрузка...';
    return `${user.lastName} ${user.firstName}`;
  };

  // Функция для получения должности
  const getUserPosition = () => {
    if (!user) return 'Загрузка...';
    return user.role || 'Квалифицированный управляющий';
  };

  // Группировка операций по датам
  const groupOperationsByDate = (operations) => {
    const grouped = {};
    
    operations.forEach(operation => {
      const date = new Date(operation.created_at);
      const dateKey = date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      
      grouped[dateKey].push(operation);
    });
    
    return Object.keys(grouped).map(date => ({
      date,
      operations: grouped[date]
    })).sort((a, b) => new Date(b.operations[0].created_at) - new Date(a.operations[0].created_at));
  };

  // Группировка доходов по датам (используем to_date)
  const groupProfitsByDate = (profits) => {
    const grouped = {};
    
    profits.forEach(profit => {
      // Парсим дату из формата DD.MM.YYYY
      const dateParts = profit.to_date ? profit.to_date.split('.') : [];
      if (dateParts.length === 3) {
        const date = new Date(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
        const dateKey = date.toLocaleDateString('ru-RU', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
        
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        
        grouped[dateKey].push(profit);
      }
    });
    
    return Object.keys(grouped).map(date => ({
      date,
      profits: grouped[date]
    })).sort((a, b) => {
      // Сортируем по дате (более поздние первыми)
      const dateA = new Date(a.profits[0].to_date.split('.').reverse().join('-'));
      const dateB = new Date(b.profits[0].to_date.split('.').reverse().join('-'));
      return dateB - dateA;
    });
  };

  // Загрузка профиля пользователя при монтировании
  useEffect(() => {
    if (!checkAuth()) {
      return;
    }

    const loadUser = async () => {
      try {
        const userData = await fetchUserProfile();
        setUser(userData);
      } catch (error) {
        console.error('Ошибка при загрузке профиля:', error);
        if (handleAuthError(error)) {
          return;
        }
      }
    };

    loadUser();
  }, []);

  // Загрузка операций при смене вкладки
  useEffect(() => {
    if (!checkAuth()) {
      return;
    }

    const loadData = async () => {
      try {
        setIsLoading(true);
        
        switch (activeTab) {
          case 1: // Доходы
            const profitsData = await fetchProfits();
            setProfits(profitsData);
            setOperations([]);
            break;
          default: // Все операции
            const operationsData = await fetchOperations();
            setOperations(operationsData);
            setProfits([]);
            break;
        }
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
        
        if (handleAuthError(error)) {
          return;
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [activeTab]);

  const tabs = [
    { id: 0, name: 'Все операции' },
    { id: 1, name: 'Доходы' }
  ];

  const getCurrentOperations = () => {
    if (activeTab === 1) {
      // Для вкладки "Доходы" возвращаем доходы
      return groupProfitsByDate(profits);
    }
    // Для вкладки "Все операции" возвращаем операции
    return groupOperationsByDate(operations);
  };

  const renderOperation = (operation) => (
    <OperationCard key={operation.id} operation={operation} />
  );

  const renderProfit = (profit) => (
    <ProfitCard key={profit.id} profit={profit} />
  );

  const renderOperationsBlock = (block) => {
    if (activeTab === 1) {
      // Для доходов
      return (
        <div key={block.date} data-fls-block="" className="body-history__block">
          <h4 className="body-history__title body-history__title--title-20">{block.date}</h4>
          <div className="body-history__items">
            {block.profits.map(renderProfit)}
          </div>
        </div>
      );
    }
    // Для операций
    return (
      <div key={block.date} data-fls-block="" className="body-history__block">
        <h4 className="body-history__title body-history__title--title-20">{block.date}</h4>
        <div className="body-history__items">
          {block.operations.map(renderOperation)}
        </div>
      </div>
    );
  };

  // Показываем загрузку пока данные не загружены
  if (isLoading) {
    return (
      <div className="wrapper">
        <div className="loading">
          <p>Загрузка истории операций...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper">
      <header data-fls-header-accaunt="" className="header-accaunt">
        <div className="header-accaunt__container">
          <div className="header-accaunt__body">
            <a href="/account" className="header-accaunt__logo">
              <img src="/assets/img/logo.svg" alt="Logo" />
            </a>
            <div className="header-accaunt__actions">
              <div className="header-accaunt__settings">
                <a href="/account/setting" className="--icon-settings"></a>
              </div>
              <div className="header-accaunt__profile">
                <a href="" className="--icon-profile">
                  <div className="header-accaunt__content-profile">
                    <span>{getUserDisplayName()}</span>
                    <span>{getUserPosition()}</span>
                  </div>
                </a>
              </div>
              <button 
                data-fls-button="" 
                type="button" 
                className="header-accaunt__logout header-accaunt__logout--icon-log-out"
                onClick={logout}
              ></button>
            </div>
          </div>
        </div>
      </header>
      <main className="page account">
        <div className="account__container">
          <div className="account__body">
            <nav data-fls-block="" data-fls-aside="" className="aside">
              <ul className="aside__list">
                <li className="aside__item">
                  <a href="/account" className="aside__link aside__link--icon-home">Главная</a>
                </li>
                <li className="aside__item">
                  <a href="/account/expense" className="aside__link aside__link--icon-wallet">Счета</a>
                </li>
                <li className="aside__item">
                  <a href="/account/history" className="aside__link aside__link--active aside__link--icon-history">История</a>
                </li>
                <li className="aside__item">
                  <a href="/account/withdraw" className="aside__link aside__link--icon-withdraw">Вывести</a>
                </li>
              </ul>
              <div className="aside__actions">
                <a href="/account/withdraw" className="aside__action aside__action--icon-withdraw">Вывести деньги</a>
                <a href="/account/check" className="aside__action aside__action--icon-deposit">Пополнить баланс</a>
              </div>
            </nav>
            <div className="account__content">
              <h3 className="account__title account__title--title-25">История</h3>
              <div data-fls-tabs="" data-fls-tabs-animate="" className="account__history tabs-history">
                <nav data-fls-tabs-titles="" className="tabs-history__navigation">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`tabs-history__title ${activeTab === tab.id ? '--tab-active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.name}
                    </button>
                  ))}
                </nav>
                <div data-fls-tabs-body="" className="tabs-history__content">
                  <div className="tabs-history__body body-history">
                    {getCurrentOperations().length > 0 ? (
                      getCurrentOperations().map(renderOperationsBlock)
                    ) : (
                      <div className="body-history__empty">
                        <p>{activeTab === 1 ? 'Доходов не найдено' : 'Операций не найдено'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountHistory;